import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import { AppContext } from "App";
import Button from "components/base/Button";
import { Alert } from "components/base/Toast";
import { useModals } from "ModalProvider";
import { useContext, useState } from "react";
import { SAVE_PRODUCT } from "./graphql";
import ProductForm from "./ProductForm";
import SendProductEmailView from "./SendProductEmailView";

const DEFAULT_VALUES = {
  size: {
    shape: "cube",
    ballDiameter: 0,
    cubeX: 0,
    cubeY: 0,
    cubeZ: 0,
    flatX: 0,
    flatY: 0,
    cylindricalDiameter: 0,
    cylindricalHeight: 0,
    multiSizes: "",
  },
  activePrices: [],
  children: [],
  complianceCertificates: [],
  labTestReports: [],
  innerBoxX: 0,
  innerBoxY: 0,
  innerBoxZ: 0,
  displayBoxX: 0,
  displayBoxY: 0,
  displayBoxZ: 0,
  innerCartonX: 0,
  innerCartonY: 0,
  innerCartonZ: 0,
  outerCartonX: 0,
  outerCartonY: 0,
  outerCartonZ: 0,
  quantityPerDisplayBox: 0,
  quantityPerCarton: 0,
  ctnNetWeight: 0,
  ctnGrossWeight: 0,
};

const EditProductView = ({
  id,
  initialValues = {},
  onSaved,
  duplicateProduct,
}) => {
  const [product, setProduct] = useState({
    ...DEFAULT_VALUES,
    ...initialValues,
  });
  const [save, saveRes] = useMutation(SAVE_PRODUCT, {
    onCompleted(res) {
      Alert("success", "✅ Product Saved.");

      const newActivePrices = res.saveProduct.product.activePrices.map(
        (price) => ({
          id: price.id,
          company: price.company,
          currency: price.currency,
          price: price.price,
          minQty: price.minQty,
          supplierUpdatedAt: price.supplierUpdatedAt,
          archivedAt: price.archivedAt,
          archivedCode: price.archivedCode,
          archivedNotes: price.archivedNotes,
          factoryId: price.factory?.id || null,
          factoryOdooId: price.factory?.odooId || null,
        }),
      );

      setProduct((prev) => ({
        ...prev,
        activePrices: newActivePrices,
      }));

      if (onSaved) onSaved(res.saveProduct.product);
    },
    onError(error) {
      Alert("error", parseError(error));
    },
    refetchQueries: ["FETCH_NEW_PRODUCTS", "FETCH_WIS_PRODUCTS"],
  });

  const modal = useModals();

  function validate() {
    if (!product.name) {
      window.alert("Please enter product name");
      return false;
    }
    if (!product.number) {
      window.alert("Please enter product number");
      return false;
    }
    // check cost if every price has factory.
    for (const price of product.activePrices) {
      if (!price.factoryId) {
        window.alert(
          "Please select factory for all active prices. If the price is not ready, please delete it.",
        );
        return false;
      }
    }

    // Check bom if every child has product and qty > 0
    for (const child of product.children) {
      if (!child.product) {
        window.alert(
          "Please select product for all bom children. If the child is not ready, please delete it.",
        );
        return false;
      }
      if (!child.qty || child.qty <= 0) {
        window.alert(
          "Please enter valid qty for all bom children. If the child is not ready, please delete it.",
        );
        return false;
      }
    }

    return true;
  }

  function saveHandler() {
    if (!validate()) return;

    save({
      variables: {
        id,
        simpleFields: {
          // Basic Info
          name: product.name,
          nameCn: product.nameCn,
          number: product.number,
          version: product.version,
          countryCodeOfOrigin: product.countryCodeOfOrigin,
          ...product.size,
          itemWeight: product.itemWeight,
          grossWeight: product.grossWeight,
          productMaterial: product.productMaterial,
          defaultSupplierId: product.defaultSupplierId,
          producerId: product.producerId,
          deliverToSupplierId: product.deliverToSupplierId,
          defaultQty: product.defaultQty,
          moq: product.moq,
          itemsPerSet: product.itemsPerSet,

          // Company Info
          company: product.company,

          // Odoo
          odooId: product.odooId || null,
          odooIdInc: product.odooIdInc || null,

          // Production
          considerBomInProduction: product.considerBomInProduction,
          productionCategoryId: product.productionCategoryId,
          productionId: product.productionId,

          // Packing and Shipment
          packing: product.packing,
          packingName: product.packingName,
          shippingMarkName: product.shippingMarkName,

          outerCartonBarcodeEan: product.outerCartonBarcodeEan,
          outerCartonBarcodeUpc: product.outerCartonBarcodeUpc,
          innerCartonBarcodeEan: product.innerCartonBarcodeEan,
          innerCartonBarcodeUpc: product.innerCartonBarcodeUpc,
          innerBoxBarcodeEan: product.innerBoxBarcodeEan,
          innerBoxBarcodeUpc: product.innerBoxBarcodeUpc,
          innerBoxBarcodeJd: product.innerBoxBarcodeJd,
          outerCartonBarcodeJd: product.outerCartonBarcodeJd,

          cccInfo: product.cccInfo,
          colors: product.colors,

          innerBoxX: product.innerBoxX,
          innerBoxY: product.innerBoxY,
          innerBoxZ: product.innerBoxZ,
          displayBoxX: product.displayBoxX,
          displayBoxY: product.displayBoxY,
          displayBoxZ: product.displayBoxZ,
          innerCartonX: product.innerCartonX,
          innerCartonY: product.innerCartonY,
          innerCartonZ: product.innerCartonZ,
          outerCartonX: product.outerCartonX,
          outerCartonY: product.outerCartonY,
          outerCartonZ: product.outerCartonZ,
          quantityPerDisplayBox: product.quantityPerDisplayBox,
          quantityPerCarton: product.quantityPerCarton,
          ctnNetWeight: product.ctnNetWeight,
          ctnGrossWeight: product.ctnGrossWeight,

          // State
          isActive: product.isActive,
          isActiveForBiz: product.isActiveForBiz,
          isSalable: product.isSalable,
          isPurchasable: product.isPurchasable,

          // Related Items
          productLineId: product.productLineId,
          productListId: product.productListId,
          gtmProjectId: product.gtmProjectId,
        },
        certificateIds: (product.complianceCertificates || []).map(
          (certificate) => certificate.id,
        ),
        testReportIds: (product.labTestReports || []).map(
          (report) => report.id,
        ),

        // Cost
        activePrices: product.activePrices.map((i) => ({
          id: i.id,
          company: i.company,
          factoryId: i.factoryId,
          minQty: i.minQty,
          price: i.price,
          currency: i.currency,
          supplierUpdatedAt: i.supplierUpdatedAt,
        })),
        costSuppliers: product.costSuppliers,

        // BOM
        children: product.children.map((i) => ({
          id: i.id,
          productId: i.product.id,
          qty: i.qty,
        })),
      },
    });
  }

  const { user } = useContext(AppContext);

  function sendProductEmailHandler() {
    modal.present({
      title: "Send Product Email",
      children: (
        <SendProductEmailView user={user} product={product} hide={modal.hide} />
      ),
    });
  }

  return (
    <div>
      <div className="p-10 pt-6 text-xs pb-24">
        <ProductForm id={id} product={product} setProduct={setProduct} />
      </div>

      <div className="fixed bottom-0 w-full py-4 backdrop-filter backdrop-blur-lg bg-gray-100 dark:bg-gray-800 bg-opacity-50 px-10 border-t border-gray-200 dark:border-gray-700 flex space-x-6">
        <Button onClick={saveHandler} loading={saveRes.loading}>
          Save
        </Button>

        {duplicateProduct && (
          <Button
            onClick={() =>
              duplicateProduct({
                ...product,
                name: `Duplicated of ${product.name}`,
                odooId: null,
                odooIdInc: null,
                activePrices: product.activePrices.map((i) => ({
                  ...i,
                  id: null,
                })),
              })
            }
          >
            Duplicate
          </Button>
        )}

        {id && (
          <>
            <a
              href={
                process.env.REACT_APP_SERVER_ADMIN_URL + "product/product/" + id
              }
              target="_blank"
            >
              Admin
            </a>
            <Button onClick={sendProductEmailHandler}>
              Send Product Email
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default EditProductView;
